import dayjs from 'dayjs'
import { NextRouter } from 'next/router'
import { isServer } from '..'
import { isEmpty, toQueryString } from '../general/browserHelper'
import { ProcessedData } from './types'
import { Card, FareType, QueryParamsData, SearchData, SearchFareRefs } from '@/types/srp'
import { getFormattedDate } from '../general/date'
import { srpQueryObject } from '@/components/oldComponents/Dropdown/HomeSearchContainer'
import Router from 'next/router'
import { getBenefitProvided, getFaresInfo, getLeadFareIdForCard, getSpecialRTFareContext } from './helper'
import { ravenSDKSrpCfwTrigger } from '@/analytics/raven/ravenSDKHelper'
import { getCouponDisplayAndDiscountValue, getCreateCallPayloadForCfw } from './helper'
import { createItineraryApi } from '@/api/srp'
import { getCookie } from '../general/cookie'
import { getUserAuthValues } from '../general/user'

import { isWowPricingEnabled } from '@/utils/wowPricing'
import { CT_UPGRADE_BENEFIT_TYPE } from '@/utils/flights/smbVasCoupon'
import { isWowTest2CleartripVariant } from './wow'
import {
    BRANDED_FARE_FAMILY,
    DEMAND,
    ARRIVAL_DEPARTURE_OVERLAP_TIME,
    ARRIVAL_DEPARTURE_THRESHOLD,
    FARE_INCLUSIONS,
    OVERLAP_ERROR_MESSAGE,
    THIRTY_MINS_OVERLAP_ERROR_MESSAGE
} from '@/constants/Srp'
import { getUtmSourceInfo } from '@/utils/common'


export function updateRecentSearch(k: string, v: string) {
    try {
        if (!isServer() && typeof window.localStorage != 'undefined') {
            let storageName = 'recentAirport'
            let recentSearch = JSON.parse(localStorage.getItem(storageName) || '{}')
            if (isEmpty(recentSearch)) {
                recentSearch = []
            }

            let eleFound = false
            if (recentSearch.length) {
                for (let i = 0; i < recentSearch.length; i++) {
                    if (recentSearch[i].k == k) {
                        eleFound = true
                        break
                    }
                }
            }
            if (!eleFound) {
                recentSearch.unshift({ k, v })
            }
            if (recentSearch.length >= 10) {
                recentSearch.length = 10
            }
            localStorage.setItem(storageName, JSON.stringify(recentSearch))
        }
    } catch (e) {
        console.log('updateRecentSearch LC Error', e)
    }
}

export const checkInternalCTUser = () => {
    const usermisc = getCookie('usermisc') || ''
    const { email: signedInEmail = '' } = getUserAuthValues()
    if (usermisc.includes('SIGNED_IN') && !isEmpty(signedInEmail) && signedInEmail.indexOf('cleartrip.com') > -1) {
        return true
    }
    return false
}

export function getRecentSearch() {
    try {
        if (!isServer() && typeof window.localStorage != 'undefined') {
            let storageName = 'recentAirport'
            let recentSearch = JSON.parse(localStorage.getItem(storageName) || '{}')
            if (isEmpty(recentSearch)) {
                recentSearch = []
            }
            return recentSearch
        } else {
            return []
        }
    } catch (e) {
        console.log('getRecentSearch LC Error', e)
        return []
    }
}

export const formAirportList = (airportList, code) => {
    let intl = true
    let label = ''
    for (let i = 0; i < airportList.length; i++) {
        if (airportList[i].airportCode === code) {
            const { airportCity, countryCode } = airportList[i] || {}
            intl = airportList[i].intl
            label = `${code} - ${airportCity}, ${countryCode}`
            break
        }
    }
    return { intl, label }
}

export const pushSearchQueryUrl = (searchQuery: srpQueryObject) => {
    let pathname = `/flights/results`
    if (searchQuery.intl && searchQuery.intl === 'y') {
        pathname = `/flights/international/results`
    }
    Router.push({ pathname, query: { ...searchQuery } })
}

/** URL `sft` is matched case-insensitively; spaces become underscores. Invalid / empty → '' (no search flag). */
export const normalizeSftQueryValue = (
    raw: string | string[] | undefined
): '' | 'student' | 'senior_citizen' | 'army' => {
    if (raw === undefined || raw === null) return ''
    const s = Array.isArray(raw) ? raw[0] : raw
    const trimmed = String(s).trim()
    if (!trimmed) return ''
    const key = trimmed.toLowerCase().replace(/\s+/g, '_')
    if (key === 'student' || key === 'student_pass') return 'student'
    if (key === 'senior_citizen') return 'senior_citizen'
    if (key === 'army') return 'army'
    return ''
}

export const processQueryParams = (router: NextRouter, data: QueryParamsData): ProcessedData => {
    const processedData: ProcessedData = {
        isIntl: false,
        srpFlow: '',
        departDate: '',
        returnDate: '',
        source: '',
        destination: '',
        sourceHeader: '',
        destinationHeader: '',
        travellers: {
            adults: 0,
            children: 0,
            infants: 0
        },
        tripType: '',
        flightClass: '',
        dx: '',
        isCfw: false,
        carrier: '',
        sft: '',
        isMultiFare: false,
        isFFSC: false,
        isV2: true
    }

    const isIntl = router?.route?.indexOf('international') > 0 || data.intl == 'y' ? true : false
    processedData.isIntl = isIntl
    processedData.srpFlow = data.srpFlow
    processedData.departDate = decodeURIComponent(getFormattedDate(data.depart_date, 'DD/MM/YYYY', 'D/M/YYYY'))
    processedData.returnDate = data.return_date
        ? decodeURIComponent(getFormattedDate(data.return_date, 'DD/MM/YYYY', 'D/M/YYYY'))
        : ''
    processedData.source = data.from?.toUpperCase()
    processedData.destination = data.to?.toUpperCase()
    processedData.sourceHeader = data.from_header || data.from
    processedData.destinationHeader = data.to_header || data.to

    processedData.travellers = {
        adults: Number(data.adults),
        children: Number(data.childs),
        infants: Number(data.infants)
    }

    processedData.tripType = processedData.returnDate ? 'roundtrip' : 'oneway'

    if (data.carrier) {
        processedData.carrier = data.carrier
    }
    processedData.sft = normalizeSftQueryValue(data.sft)
    processedData.flightClass = data.class
    processedData.dx = data.dx || ''
    processedData.isCfw = data.isCfw === true || data.isCfw === 'true'
    processedData.isMultiFare = data.isMultiFare === true || data.isMultiFare === 'true'
    processedData.isFFSC = data.isFFSC === true || data.isFFSC === 'true'
    return processedData
}

export const getSearchQueryObj = (query: ProcessedData) => {
    const {
        source,
        destination,
        departDate,
        returnDate,
        flightClass = 'Economy',
        travellers,
        sourceHeader,
        destinationHeader,
        isIntl,
        carrier,
        sft,
        isCfw,
        isMultiFare,
        isFFSC,
        isV2
    } = query
    const { adults = 1, children = 0, infants = 0 } = travellers
    const utmPayload = getUtmSourceInfo()
    // let returnDateQuery = ''
    // let carrierQuery = ''
    const queryObj: Record<string, unknown> = {
        from: source,
        source_header: sourceHeader,
        to: destination,
        destination_header: destinationHeader,
        depart_date: departDate,
        class: flightClass,
        adults,
        childs: children,
        infants,
        mobileApp: true,
        intl: isIntl ? 'y' : 'n',
        responseType: 'jsonV3',
        source: 'DESKTOP',
        utm_currency: 'INR',
        return_date: '',
        carrier: '',
        cfw: false,
        multiFare: true,
        isFFSC: true,
        ...utmPayload
    }
    const apiSft = sft ? convertSftToFareCategory(sft) : null
    if (apiSft) {
        queryObj.sft = apiSft
    }
    if (returnDate) {
        queryObj.return_date = returnDate
    }
    if (carrier) {
        queryObj.carrier = carrier
    }
    if (isV2) {
        queryObj.filterVersion = "v2"
    }
    if (isCfw) {
        queryObj.cfw = true
    }
    return queryObj
}

export const convertSftToFareCategory = (
    sftFare: string
): 'STUDENT' | 'SENIOR_CITIZEN' | 'ARMY' | null => {
    switch (sftFare) {
        case 'student':
            return 'STUDENT'
        case 'senior_citizen':
            return 'SENIOR_CITIZEN'
        case 'army':
            return 'ARMY'
        default:
            return null
    }
}

export const searchQueryUrl = (query: ProcessedData, wowPricingVariant: string = '') => {
    const queryObj = getSearchQueryObj(query)
    const isIntl = !!query?.isIntl

    // Pass `isWP=true` so the search backend knows to return WOW-aware data.
    // Mirrors flights-pwa src/utils/flights/results.ts.
    const isWP = isWowPricingEnabled(wowPricingVariant, !isIntl)
    if (isWP) {
        queryObj.isWP = true
    }
    if (isWowTest2CleartripVariant(wowPricingVariant, isIntl)) {
        queryObj.enableWowSavingsOnIntl = true
    }
    return toQueryString(queryObj)
}

export const getMerchandisingQueryFK = (isIntl: boolean, tripType: string): string => {
    const type = isIntl ? 'intl' : 'dom'
    return `${type}_${tripType.toLowerCase()}`
}

export function getHighRpsDataFF(
    hightRpsIds: any,
    cards: any,
    fares: any,
    subTravelOptions: any,
    isPerAdult: boolean
): any {
    const highRpsCards = cards.filter((card: any) => {
        return hightRpsIds.includes(card.travelOptionId)
    })

    const highRpsDiscountedPrice =
        getFaresInfo(highRpsCards[0], subTravelOptions, fares)?.[0]?.pricing?.[
            isPerAdult ? 'perAdultPricing' : 'totalPricing'
        ]?.couponDetails?.discountedPrice || 0

    return { highRpsCards, highRpsDiscountedPrice }
}

/**
 * Builds the /flight/fetch-interstitial-page request payload.
 *
 * IMPORTANT (fix, post-review): the previous version sent a flat
 * `travelOptions: [{ J1: { travelOptionId } }]` array — no subTravelOptionId,
 * no fareId, no price. The real, platform-agnostic BE contract (confirmed
 * against flights-pwa's already-BE-integrated `getSrpFareRequestPayload` in
 * `src/utils/flights/search/index.tsx`, and matching Appendix C of the
 * architecture doc) instead wants `selectedTravelOptions` — an object keyed
 * `J1`/`J2`, each carrying `selectedSubTravelOptions: [{subTravelOptionId,
 * fareId, price}]`. BE likely needs `price` to compute the upgrade-callout
 * amount/eligibility relative to what's already selected, so this shape
 * matters for PE specifically, not just as a style preference.
 */
export const getSrpFareRequestPayload = (
    selectedCards: Card[],
    searchData: SearchData,
    isCfw = false,
    isInternational: boolean,
    isPE = false,
    isMultiFare = false,
    isSpecialRT = false,
    searchFareRefs: SearchFareRefs = {},
    isWP = false,
    loyaltySearchDetails: { programContext?: SearchData['programContext'] } | null = null
) => {
    const {
        searchIntent = {},
        dataId = "",
        searchId = "",
        supercoins = {},
        subTravelOptions = {},
        fares = {},
        specialRTFareMap = {},
        travelOptionIdsToFareIdsMap = {},
        programContext: searchProgramContext
    } = searchData || {}
    const programContext = loyaltySearchDetails?.programContext ?? searchProgramContext
    const { trafficSource, utmCustom } = getUtmSourceInfo()

    /** Special-RT parity with flights-pwa: the BE only returns the combined multi-cabin SRT fares
     *  (specialRTFareMap / travelOptionIdsToFareIdsMap populated, per-cabin cabinClassSummary) when we
     *  echo back the *combined* special-RT fareId for both legs — the exact same fareId + combined price
     *  on J1 and J2. If instead we send each leg's per-leg one-way cheapestFareId (the default below),
     *  the BE treats the request as a regular RT and responds with per-leg data and empty SRT maps, so
     *  the interstitial can only ever show the single searched cabin. The combined fareId comes from the
     *  search-time specialRTFareMap (falling back to travelOptionIdsToFareIdsMap), keyed by the
     *  "[onwardTravelOptionId, returnTravelOptionId]" pair. */
    const specialRtKey =
        isSpecialRT && (selectedCards?.length || 0) > 1
            ? `[${selectedCards[0]?.travelOptionId}, ${selectedCards[1]?.travelOptionId}]`
            : ''
    const specialRtFareIds = specialRtKey
        ? specialRTFareMap?.[specialRtKey]?.fareIds || travelOptionIdsToFareIdsMap?.[specialRtKey] || []
        : []
    const specialRtCombinedFareId = specialRtFareIds?.[0] || ''
    const specialRtCombinedPrice = specialRtCombinedFareId
        ? fares?.[specialRtCombinedFareId]?.pricing?.totalPricing?.totalPrice ?? ''
        : ''

    const selectedTravelOptions: Record<string, any> = {}
    const selectedCardIds: number[] = []
    selectedCards?.forEach((card, index) => {
        const { travelOptionId = '', subTravelOptionIds = [], cardId = 0 } = card || {}
        selectedCardIds.push(cardId)
        const selectedSubTravelOptions = (subTravelOptionIds || []).map((subTravelOptionId: string) => {
            if (specialRtCombinedFareId) {
                return { subTravelOptionId, fareId: specialRtCombinedFareId, price: specialRtCombinedPrice }
            }
            /** Read the search-time snapshot (srpSlice.searchFareRefs) and nothing else.
             *  Both `subTravelOptions` and `fares` are rewritten by `mergeInterstitialData`, so
             *  resolving either the fareId or the price from them makes this payload depend on how
             *  many interstitial responses happen to have landed already: the same modal open then
             *  produces a different request the second time, which is what made the response shape
             *  (and therefore the cache) unpredictable. A special-RT response is the worst case —
             *  it describes the trip through its combined fare-combo map rather than per-leg fares,
             *  so afterwards a leg it covered resolves to no fareId at all and BE rejects the next
             *  regular-RT request with a 400.
             *
             *  `buildSearchFareRefs` covers every subTravelOption in the search response, and cards
             *  only ever reference those, so the fallback below is unreachable in practice — it
             *  exists so a malformed response degrades instead of sending an empty fareId. */
            const searchFareRef = searchFareRefs?.[subTravelOptionId]
            if (searchFareRef) {
                return { subTravelOptionId, fareId: searchFareRef.fareId, price: searchFareRef.price }
            }
            const { cheapestFareId = '', fareIds = [] } = subTravelOptions?.[subTravelOptionId] || {}
            const fareId = cheapestFareId || fareIds?.[0] || ''
            const price = fares?.[fareId]?.pricing?.totalPricing?.totalPrice ?? ''
            return { subTravelOptionId, fareId, price }
        })
        selectedTravelOptions[`J${index + 1}`] = {
            travelOptionId,
            selectedSubTravelOptions
        }
    })
    return {
        cfwFlow: isCfw,
        dataId,
        searchId,
        searchIntent,
        selectedTravelOptions,
        supercoins,
        international: isInternational ? true : false,
        isMultiFare,
        isSpecialRT,
        /** No WOW-pricing experiment exists on desktop today (mobile-only concept) — sent as a static
         *  false to match the contract shape; wire a real variant here if/when desktop gets one. */
        isWP,
        /** pe_discovery_v1 — BE uses this to attach cabinClassSummary / upgradeCallout on the response.
         *  Mirrors flights-pwa's real, BE-integrated request contract. Caller passes this in (see
         *  FareFamilyModal's `isPeDiscoveryEnabled()` call) rather than this module computing it itself,
         *  to avoid a circular import: this file is imported by srpSlice (a reducer), and
         *  `isPeDiscoveryEnabled()` needs the Redux store — importing it here would cycle back
         *  through configureStore -> reducers -> srpSlice -> this file. */
        isPE,
        trafficSource,
        utmCustom,
        metaDataContext: {
            trackingDetails: {
                selectedCardIds
            }
        },
        ...(programContext ? { programContext } : {}),
    }
}

export const getFFBottomCallout = (fare: FareType) => {
    const { benefitType, displayText: benefitText } =
        fare.benefitTags?.filter((t: any) => t.benefitType === CT_UPGRADE_BENEFIT_TYPE)?.[0] || {}

    const additionalBenefitsWorth = fare.additionalBenefitWorth

    if (!benefitType && !additionalBenefitsWorth) return null

    return {
        benefitType,
        benefitText,
        additionalBenefitsWorth
    }
}

export const hasAdditionalBenefitsWorth = (fare: FareType) => {
    const callout = getFFBottomCallout(fare)
    return Boolean(callout?.additionalBenefitsWorth && !callout.benefitType)
}

export const checkIsInterstitialDataPresent = (searchData: SearchData, travelOptionId: string) => {
    const { fares, subTravelOptions } = searchData || {}
    const subTravelOption = subTravelOptions?.[travelOptionId]
    if (!subTravelOption?.fareList?.length) return false

    const totalFareCount = subTravelOption.totalFareCount

    if (subTravelOption.fareList.length !== totalFareCount) return false

    for (let fare of subTravelOption.fareList) {
        if (!fares[fare.fareId]) return false
    }

    return true
}

// if isRT and one of the selectedFares is PTC, return true
export function validatePTC(selectedFares: any[], isIntl: boolean) {
    selectedFares = selectedFares.filter(Boolean)
    
    // not RT
    if (selectedFares.length !== 2 || isIntl) return {isError: false}

    // both fares are ptc, or both are not ptc
    if ((selectedFares[0].ptcFare && selectedFares[1].ptcFare) || (!selectedFares[0].ptcFare && !selectedFares[1].ptcFare)) {
        return {isError: false}
    }

    if (selectedFares[0].ptcFare) {
        return {isError: true, message: "If you are selecting Special fare for onward journey, please select a Special fare for your return journey as well"}
    }

    return {isError: true, message: "If you are selecting Special fare for return journey, please select a Special fare for your onward journey as well"}
}

export const parseFilters = (filters: any) => {
    const filtersOW = filters?.onwardLeg!
    const filtersRT = filters?.returnLeg || {}
    const filtersCommon = filters || {}
  
    return { filtersOW, filtersRT, filtersCommon }
}

export const isCheckInBaggageEnabled = (selectedFilters) => {
    const { filtersOW, filtersRT } = parseFilters(selectedFilters)
    const { fareInclusions: { selectedValues: fareInclusionsOW = [] } = {} } = filtersOW || {}
    const { fareInclusions: { selectedValues: fareInclusionsRT = [] } = {} } = filtersRT || {}
    return {
        ow: fareInclusionsOW.includes(FARE_INCLUSIONS.BAGGAGE),
        rt: fareInclusionsRT.includes(FARE_INCLUSIONS.BAGGAGE)
    }
}
//create a function which takes the fare card and returns if it is a CT branded fare card
export const isCTBrandedFareCardCheck = (fareCard: Record<string, any>) => {
    return fareCard?.fareGroup === BRANDED_FARE_FAMILY && fareCard?.fareFamilyType === DEMAND
}
  //create a function which takes the fare card and returns if it is a CT branded fare card selected in onward leg
export const isCTBrandedFareCardSelectedOnward = (selectedFares: Record<string, any>) => {
    return selectedFares[0]?.fareGroup === BRANDED_FARE_FAMILY && selectedFares[0]?.fareFamilyType === DEMAND
}
  //create a function which takes the fares array and returns if it is a CT branded fare card selected in return leg
export const isCTBrandedFareCardSelectedReturn = (selectedFares: Record<string, any>) => {
    return selectedFares[1]?.fareGroup === BRANDED_FARE_FAMILY && selectedFares[1]?.fareFamilyType === DEMAND
}
export const arrangeFaresArray = (faresArray: Record<string, any>[], selectedFares: Record<string, any>, selectedLeg: number) => {
    //if the selected fare is a CT branded fare card, then modify the fares array to shift same CT branded fare card which matches the onward selected fare card fareGroup to the first position
    if(isCTBrandedFareCardSelectedOnward(selectedFares) && selectedLeg === 1){
        let newFaresArray: any[] = [...faresArray]
        const index = newFaresArray.findIndex((fare: any) => fare.brand === selectedFares[0].brand)
        if(index !== -1){
            const ctBrandedFareCard = newFaresArray[index]
            newFaresArray.splice(index, 1)
            newFaresArray.unshift(ctBrandedFareCard)
            return newFaresArray
        }
    }
    return faresArray
}

export const getRavenPayloadCTBRAND = (
    faresArray: any[],
    servicesData: any,
    baggageAllowances: any
): Record<string, string> => {
    const ctBrandedFareCard = faresArray?.find((fare: any) => isCTBrandedFareCardCheck(fare)) || {}
    if (isEmpty(ctBrandedFareCard)) return {} as Record<string, string>
    const benefitsProvidedCTBRAND = getBenefitProvided(ctBrandedFareCard, servicesData, baggageAllowances)
    const farePerks = ctBrandedFareCard?.farePerks || {}
    const { perks = [] } = farePerks || {}
    let VDC_CALLOUT = ''
    let INSURANCE_CALLOUT = ''
    if (perks?.length > 0) {
        perks.forEach((perk: any) => {
            if (perk?.type === 'visa_rejection_cover') {
                VDC_CALLOUT = perk?.title
            }
            if (perk?.type === 'travel_insurance') {
                INSURANCE_CALLOUT = perk?.title
            }
        })
    }
    return {
        a_cancellation_fee: benefitsProvidedCTBRAND.cancellation || 'N/A',
        a_date_change_fee: benefitsProvidedCTBRAND.dateChange || 'N/A',
        a_seat_callout: benefitsProvidedCTBRAND.seat || 'N/A',
        a_meal_callout: benefitsProvidedCTBRAND.meal || 'N/A',
        a_cabin_baggage_callout: benefitsProvidedCTBRAND.cabinBaggage || 'N/A',
        a_checkin_baggage_callout: benefitsProvidedCTBRAND.checkInBaggage || 'N/A',
        a_vdc_callout: VDC_CALLOUT || 'N/A',
        a_insurance_callout: INSURANCE_CALLOUT || 'N/A',
    }
}

export const getPtcFilterDetails = (selectedFilters) => {
  const { onwardLeg: onwardLegFilters, returnLeg: returnLegFilters } = selectedFilters || {}
  const { ptcFare: onwardPtcFare } = onwardLegFilters || {}
  const { ptcFare: returnPtcFare } = returnLegFilters || {}
  const resolvePtcType = (ptcFare?: { checked?: boolean; type?: string }) => {
    if (!ptcFare?.checked) return ''
    const type = ptcFare?.type
    if (type === 'student' || type === 'senior_citizen' || type === 'army') return type
    return 'student'
  }
  return {
    onwardFilterPtcType: resolvePtcType(onwardPtcFare),
    returnFilterPtcType: resolvePtcType(returnPtcFare),
  }
}
export const validateRTFlightTimings = (onwardArrivalTime: string, returnDepartureTime: string) => {
    const arrivalDate = dayjs(onwardArrivalTime)
    const departureDate = dayjs(returnDepartureTime)
    const diffMinutes = departureDate.diff(arrivalDate, 'minute')

    if (diffMinutes < ARRIVAL_DEPARTURE_OVERLAP_TIME) {
        return { valid: false, message: OVERLAP_ERROR_MESSAGE }
    }
    if (diffMinutes < ARRIVAL_DEPARTURE_THRESHOLD) {
        return { valid: false, message: THIRTY_MINS_OVERLAP_ERROR_MESSAGE }
    }
    return { valid: true }
}
